import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Stack,
  Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { getUserById, updateUser } from '../../services/api/userApi';
import { formatDateTime } from '../../utils/formatters';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<any>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () => getUserById(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => updateUser(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsEditing(false);
      setSuccessMessage('사용자 정보가 성공적으로 업데이트되었습니다.');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const handleEdit = () => {
    setEditedUser({ ...user.user });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedUser(null);
    setIsEditing(false);
  };

  const handleSave = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = () => {
    setConfirmDialogOpen(false);
    updateMutation.mutate({
      isAdmin: editedUser.isAdmin,
      blockMalware: editedUser.blockMalware,
      notifications: editedUser.notifications,
      dataCollection: editedUser.dataCollection,
      notificationEnabled: editedUser.notificationEnabled,
    });
  };

  const handleToggle = (field: string) => {
    setEditedUser((prev: any) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box>
        <Alert severity="error">사용자 정보를 불러오는데 실패했습니다.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mt: 2 }}
        >
          목록으로 돌아가기
        </Button>
      </Box>
    );
  }

  const displayUser = isEditing ? editedUser : user.user;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">사용자 상세 정보</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          variant="outlined"
        >
          목록으로
        </Button>
      </Stack>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          사용자 정보 업데이트에 실패했습니다.
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* 기본 정보 */}
        <Typography variant="h6" gutterBottom>
          기본 정보
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              사용자 ID
            </Typography>
            <Typography variant="body1">{displayUser.id}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              이메일
            </Typography>
            <Typography variant="body1">{displayUser.email}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              사용자명
            </Typography>
            <Typography variant="body1">{displayUser.username}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              로그인 방식
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={
                  displayUser.provider === 'google'
                    ? 'Google'
                    : displayUser.provider === 'kakao'
                    ? 'Kakao'
                    : '일반 (이메일)'
                }
                color={
                  displayUser.provider === 'google'
                    ? 'error'
                    : displayUser.provider === 'kakao'
                    ? 'warning'
                    : 'default'
                }
                size="small"
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              가입일
            </Typography>
            <Typography variant="body1">
              {formatDateTime(displayUser.createdAt)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              최종 수정일
            </Typography>
            <Typography variant="body1">
              {formatDateTime(displayUser.updatedAt)}
            </Typography>
          </Box>
        </Box>

        {/* 구독 정보 */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          구독 정보
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              구독 상태
            </Typography>
            <Box sx={{ mt: 1 }}>
              {!displayUser.isSubscribed ? (
                <Chip label="미구독" color="default" size="small" />
              ) : (() => {
                const expiresAt = displayUser.subscriptionExpiresAt ? new Date(displayUser.subscriptionExpiresAt) : null;
                const isExpired = expiresAt && expiresAt < new Date();
                
                if (isExpired) {
                  return <Chip label="만료" color="default" size="small" />;
                }
                
                const tierConfig: Record<string, { label: string; color: string; textColor: string }> = {
                  starter: { label: '🌱 스타터', color: '#8BC34A', textColor: '#fff' },
                  basic: { label: '🛡️ 베이직', color: '#2196F3', textColor: '#fff' },
                  silver: { label: '🥈 실버', color: '#C0C0C0', textColor: '#333' },
                  gold: { label: '🥇 골드', color: '#FFD700', textColor: '#333' },
                  platinum: { label: '💎 플래티넘', color: '#00D9FF', textColor: '#fff' },
                  diamond: { label: '💠 다이아몬드', color: '#B9F2FF', textColor: '#333' },
                };
                
                const tier = displayUser.subscriptionTier || 'starter';
                const config = tierConfig[tier] || { label: '✅ 구독중', color: '#8BC34A', textColor: '#fff' };
                
                return (
                  <Chip 
                    label={config.label} 
                    size="small"
                    sx={{
                      backgroundColor: config.color,
                      color: config.textColor,
                      fontWeight: 500,
                    }}
                  />
                );
              })()}
            </Box>
          </Box>

          {displayUser.isSubscribed && displayUser.subscriptionExpiresAt && new Date(displayUser.subscriptionExpiresAt) >= new Date() && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  구독 일수
                </Typography>
                <Typography variant="body1">
                  {displayUser.totalSubscriptionMonths 
                    ? `${displayUser.totalSubscriptionMonths}일`
                    : '정보 없음'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  구독 시작일
                </Typography>
                <Typography variant="body1">
                  {displayUser.subscriptionStartedAt 
                    ? formatDateTime(displayUser.subscriptionStartedAt)
                    : '정보 없음'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  구독 만료일
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(displayUser.subscriptionExpiresAt)}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* 권한 및 설정 */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          권한 및 설정
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={displayUser.isAdmin}
                  onChange={() => handleToggle('isAdmin')}
                  disabled={!isEditing}
                />
              }
              label="관리자 권한"
            />
            {displayUser.isAdmin && (
              <Chip label="관리자" color="primary" size="small" sx={{ ml: 1 }} />
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={displayUser.notificationEnabled}
                  onChange={() => handleToggle('notificationEnabled')}
                  disabled={!isEditing}
                />
              }
              label="알림 활성화"
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={displayUser.blockMalware}
                  onChange={() => handleToggle('blockMalware')}
                  disabled={!isEditing}
                />
              }
              label="악성앱 차단"
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={displayUser.notifications}
                  onChange={() => handleToggle('notifications')}
                  disabled={!isEditing}
                />
              }
              label="푸시 알림 수신"
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={displayUser.dataCollection}
                  onChange={() => handleToggle('dataCollection')}
                  disabled={!isEditing}
                />
              }
              label="데이터 수집 동의"
            />
          </Box>
        </Box>

        {/* FCM 토큰 정보 */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          디바이스 정보
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            FCM 토큰
          </Typography>
          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              bgcolor: 'grey.100',
              p: 1,
              borderRadius: 1,
            }}
          >
            {displayUser.fcmToken || '등록된 토큰 없음'}
          </Typography>
        </Box>

        {/* 활동 정보 */}
        {user.phoneReports && user.phoneReports.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              전화번호 신고 내역
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2">
              총 {user.phoneReports.length}건의 신고
            </Typography>
          </Box>
        )}

        {user.victimProfiles && user.victimProfiles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              피해 신고 내역
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2">
              총 {user.victimProfiles.length}건의 피해 신고
            </Typography>
          </Box>
        )}

        {/* 액션 버튼 */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          {!isEditing ? (
            <Button variant="contained" onClick={handleEdit}>
              수정
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={handleCancel}>
                취소
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? '저장 중...' : '저장'}
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="사용자 정보 수정"
        message="사용자 정보를 수정하시겠습니까?"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmDialogOpen(false)}
      />
    </Box>
  );
};

export default UserDetailPage;
